import PptxGenJS from 'pptxgenjs';
import MenuType from '@app/types/data/base/MenuType';
import IMenuItem from '@app/types/data/base/IMenuItem';
import { colors } from '@app/services/export/ppt/variables/constants';
import { copyBold, copyMini, copySmall } from '@app/services/export/ppt/variables/typography';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import GlobalsStoreModule from '@app/store/modules/GlobalsStoreModule';
import { AbstractPptComponent, IPptComponentPosition } from '@app/services/export/ppt/classes/AbstractPptComponent';
import TableProps = PptxGenJS.TableProps;
import TableRow = PptxGenJS.TableRow;
import TextProps = PptxGenJS.TextProps;

interface IProps {
	type?: MenuType;
	headline: string;
	list: IMenuItem[];
}

export class Menu extends AbstractPptComponent {
	protected readonly slide: PptxGenJS.Slide;

	protected readonly props: IProps;

	public static readonly headerHeight = 0.3;

	public static readonly rowHeight = 0.65;

	protected readonly i18n = getStoreModule(GlobalsStoreModule).i18n;

	public constructor(slide: PptxGenJS.Slide, props: IProps, position: IPptComponentPosition) {
		super(position);
		this.slide = slide;
		this.props = props;
	}

	public create(): void {
		const tableRows: TableRow[] = [];
		const tableOptions: TableProps = {
			x: this.position.x,
			y: this.position.y,
			w: this.position.w,
			rowH: [Menu.headerHeight, ...Array(this.props.list.length).fill(Menu.rowHeight)],
			margin: [0, 0.1, 0, 0.1],
			border: [{ type: 'none' }, { type: 'none' }, { type: 'solid', color: colors.white, pt: 0 }, { type: 'none' }]
		};

		// header
		const tableHeader: TableRow = [];

		if (this.props.headline) {
			tableHeader.push({
				text: this.props.headline,
				options: { ...copyBold, color: colors.blue, valign: 'top', fill: { color: colors.white }, margin: 0 }
			});
		}

		if (this.hasSomeBookingCode()) {
			const bookingCodeColWidth = 0.9;
			tableOptions.colW = [this.position.w - bookingCodeColWidth, bookingCodeColWidth];

			tableHeader.push({
				text: this.i18n.bookingCode,
				options: {
					...copySmall,
					color: colors.blue,
					align: 'right',
					valign: 'top',
					fill: { color: colors.white },
					margin: [0, 0.1, 0, 0]
				}
			});
		}

		tableRows.push(tableHeader);

		// rows
		this.props.list.forEach((item: IMenuItem): void => {
			const tableRow: TableRow = [];

			if (item.title) {
				const titleTextArray: TextProps[] = [{ text: item.title, options: { ...copyBold, breakLine: true } }];

				if (item.description) {
					titleTextArray.push({ text: item.description, options: { ...copyMini } });
				}

				tableRow.push({
					text: titleTextArray,
					options: { color: colors.blue, valign: 'middle', fill: { color: colors.lightGray } }
				});
			}

			if (item.bookingCode) {
				tableRow.push({
					text: item.bookingCode,
					options: {
						...copySmall,
						color: colors.blue,
						valign: 'middle',
						align: 'right',
						fill: { color: colors.lightGray }
					}
				});
			}

			tableRows.push(tableRow);
		});

		this.slide.addTable(tableRows, tableOptions);
	}

	protected getHeight(): number {
		return Menu.headerHeight + this.props.list.length * Menu.rowHeight;
	}

	private hasSomeBookingCode(): boolean {
		return this.props.list.some((item: IMenuItem): boolean => !!item.bookingCode);
	}
}
